import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';

import GlobalTitles from '@/features/global-home/components/GlobalTitles.vue';
import ListToggle from '@/features/sponsored-recommendations/components/ListToggle.vue';
import SponsoredLabel from '@/features/sponsored-recommendations/components/SponsoredLabel.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import WatchNowButton from '@/components/buttons/WatchNowButton.vue';

import type { GetGlobalHomeQuery } from '@/features/global-home/components/graphql/queries/GetGlobalHome.query';
import type {
	ListSponsoredAd,
	ListTitleSR,
	SRClickEvent,
	SRClickTitleEvent,
} from '@/features/sponsored-recommendations/types';
import type { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { Maybe } from '@/helpers/types';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useListActions } from '@/helpers/composables/useListActions';

import { SraEventAction } from '@/@types/graphql-types';

/** GlobalTitles' featured shape - the only one with scoring and a full watchNowOffer. */
type GlobalTitleEdges = NonNullable<NonNullable<GetGlobalHomeQuery['MAIN_TOP_10_MOVIES']>['edges']>;

type GlobalTitleEvent = { index: number };

/** Events carry the source node, so only `adaptedTitles` needs a cast - never the way back out. */

export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationMultiTitleHomeGlobal',
  props: {
    sponsoredAd: null,
    lazy: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    placement: null,
    showDesktopWatchNow: { type: Boolean, default: false }
  },
  emits: ["onPosterClick", "onTrackerClickout", "onWatchNowClick", "onClickMore", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClick', data: SRClickTitleEvent<ListTitleSR>): void;
	(e: 'onTrackerClickout', data?: SRClickEvent<ListTitleSR>): void;
	/** The orchestrator owns this: firing the external clickout pixels needs its trackers and web locale. */
	(e: 'onWatchNowClick', data: SRClickEvent<ListTitleSR>): void;
	(e: 'onClickMore', data?: SRClickEvent<ListTitleSR>): void;
	(e: 'imageError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: ListSponsoredAd, lazy: boolean, fullWidth: boolean, additionalContexts: SnowplowContext[], placement?: ClickoutPlacement, showDesktopWatchNow: boolean };





const { country, language } = storeToRefs(useLanguageStore());
const { trackSraEvent } = useBackendTracking();
const { setInWatchlist } = useListActions({
	sponsoredAd: () => props.sponsoredAd,
	additionalContexts: () => props.additionalContexts,
});

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed(() => sponsoredCampaign.value?.node);
const listName = computed(() => campaignNode.value?.content.name);
const promotionalTitle = computed(() => sponsoredCampaign.value?.promotionalTitle || listName.value);
const titles = computed(() => campaignNode.value?.titles.edges?.map(edge => edge.node) ?? []);
const landingPageURL = computed(() => sponsoredCampaign.value?.watchNowOffer?.standardWebURL ?? '');

/** Every CTA points at this one campaign offer - the fragment selects none per title. */
const watchNowOffer = computed(() => sponsoredCampaign.value?.watchNowOffer ?? null);
const watchNowLabel = computed(() => sponsoredCampaign.value?.watchNowLabel);
const providerShortName = computed(() => watchNowOffer.value?.package.shortName);
const providerIconUrl = computed(
	() => sponsoredCampaign.value?.promotionalProviderLogo || watchNowOffer.value?.package.icon
);
const hasLogoOverride = computed(() => !!sponsoredCampaign.value?.promotionalProviderLogo);

/** `hideRatings` is the blanket switch; either score can also be suppressed on its own. */
const hideJwScore = computed(() => !!(sponsoredCampaign.value?.hideRatings || sponsoredCampaign.value?.hideJwScore));
const hideImdbScore = computed(
	() => !!(sponsoredCampaign.value?.hideRatings || sponsoredCampaign.value?.hideImdbScore)
);

const nodeOverrides = computed(() => {
	const overrides = sponsoredCampaign.value?.nodeOverrides ?? [];

	return new Map(
		overrides.map(({ nodeId, promotionalImageUrl, watchNowOffer }) => [
			nodeId,
			{ promotionalImageUrl, standardWebURL: watchNowOffer?.standardWebURL },
		])
	);
});

/** Mirrored locally: setInWatchlist neither writes these nodes back nor refetches, so they never update on their own. */
const watchlistCreatedAt = ref<Record<string, string | null>>({});

const adaptedTitles = computed(
	() =>
		titles.value.map(node => {
			const override = nodeOverrides.value.get(node.id);
			const createdAt = watchlistCreatedAt.value[node.id];

			return {
				node: {
					...node,
					...(node.id in watchlistCreatedAt.value && {
						watchlistEntryV2: createdAt ? { createdAt } : null,
					}),
					...(override && {
						content: {
							...node.content,
							posterUrl: override.promotionalImageUrl || node.content.posterUrl,
							// The external URL is what lets TitleDetailLink emit `clickOut`, which the SRA Clickout hangs off.
							fullPath: override.standardWebURL || node.content.fullPath,
						},
					}),
				},
			};
		}) as unknown as GlobalTitleEdges
);

function titleAt(index: number) {
	return titles.value[index];
}

function asWatchNowTitle(title: ListTitleSR) {
	return title as unknown as TitleDetail;
}

function handlePosterClick({ index }: GlobalTitleEvent) {
	const listTitle = titleAt(index);

	emits('onPosterClick', { title: listTitle, position: index });

	trackSraEvent({
		action: SraEventAction.Clickout,
		sponsoredAd: () => props.sponsoredAd,
		jwEntityID: listTitle.id,
		position: index,
	});

	emits('onTrackerClickout', { title: listTitle, position: index });
}

function handleMoreInfoClick({ index }: GlobalTitleEvent) {
	emits('onClickMore', { title: titleAt(index), position: index });
}

function handleWatchNowClick(index: number) {
	emits('onWatchNowClick', { title: titleAt(index), position: index });
}

function handleListClick() {
	trackSraEvent({ action: SraEventAction.Click, sponsoredAd: () => props.sponsoredAd });

	if (landingPageURL.value) {
		trackSraEvent({ action: SraEventAction.ListClickout, sponsoredAd: () => props.sponsoredAd });
	}

	emits('onTrackerClickout', { title: titles.value[0], position: 0 });
}

function handleFollowClick() {
	trackSraEvent({ action: SraEventAction.FollowClicked, sponsoredAd: () => props.sponsoredAd });
}

async function handleWatchlistClick({ index }: GlobalTitleEvent) {
	const listTitle = titleAt(index);

	trackSraEvent({
		action: SraEventAction.WatchlistClicked,
		sponsoredAd: () => props.sponsoredAd,
		jwEntityID: listTitle.id,
		position: index,
	});

	const updatedTitle = await setInWatchlist(
		listTitle.id,
		!!watchlistCreatedAt.value[listTitle.id],
		{ title: listTitle.content.title, objectType: listTitle.objectType, objectId: listTitle.objectId },
		{ source: 'sponsored-recommendations' }
	);
	if (!updatedTitle) return;

	watchlistCreatedAt.value = {
		...watchlistCreatedAt.value,
		[listTitle.id]: updatedTitle.data?.setInWatchlistV2?.title?.watchlistEntryV2?.createdAt ?? null,
	};
}

return { __sfc: true,props, emits, country, language, trackSraEvent, setInWatchlist, sponsoredCampaign, campaignNode, listName, promotionalTitle, titles, landingPageURL, watchNowOffer, watchNowLabel, providerShortName, providerIconUrl, hasLogoOverride, hideJwScore, hideImdbScore, nodeOverrides, watchlistCreatedAt, adaptedTitles, titleAt, asWatchNowTitle, handlePosterClick, handleMoreInfoClick, handleWatchNowClick, handleListClick, handleFollowClick, handleWatchlistClick, GlobalTitles, ListToggle, SponsoredLabel, ProviderIcon, WatchNowButton }
}

})